import 'regenerator-runtime/runtime';

const DEFAULT_BREAKPOINTS = ['40rem', '52rem', '64rem'];

const firstLeft = (arr, idx) => {
  if (idx >= arr.length) idx = arr.length - 1;

  while (idx > -1 && (arr[idx] === null || arr[idx] === undefined)) idx--;

  return arr[idx];
};

const parsePropValue = value => {
  const isNumber = typeof value === 'number';
  const isNegative = isNumber ? value < 0 : value[0] === '-';
  const baseValue = isNegative ? isNumber ? -value : value.slice(1) : value;
  return [baseValue, isNegative];
};

const negate = v => typeof v === 'number' ? -v : '-' + v;

const parseProp = (prop, theme, config) => {
  const scaleName = config.scale || config.systemProp + "Scales";
  const scale = theme[scaleName] || config.defaultScale;
  if (!scale) return prop;

  if (Array.isArray(prop)) {
    const result = [];
    const count = (theme.breakpoints || DEFAULT_BREAKPOINTS).length + 1;

    for (let i = 0; i < count; i++) {
      const [s, isNeg] = parsePropValue(firstLeft(prop, i));
      if (s === undefined) return;
      result[i] = isNeg ? negate(scale[s][i]) : scale[s][i];
    }

    return result;
  }

  const [s, isNeg] = parsePropValue(prop);
  if (s === undefined) return;
  return isNeg ? scale[s].map(negate) : scale[s];
};
/**
 * Creates a Parser using the provided scale prop configurations.
 *
 * @param configs Collection of scale configuration objects. The key of each
 * configuration determines the prop name the intercept.
 *
 * @returns A prop Parser to pass to `interceptScales`.
 */


const scales = configs => {
  const parse = props => {
    const propKeys = Object.keys(props);
    const systemProps = {};
    const theme = props.theme || {};

    for (let i = 0; i < propKeys.length; i++) {
      if (!(propKeys[i] in configs)) continue;
      const key = propKeys[i];
      const prop = props[key];
      const config = configs[key];
      const systemProp = config.systemProp;
      const val = parseProp(prop, theme, config);
      systemProps[systemProp] = val;
    }

    return Object.assign(systemProps, props);
  };

  parse.configs = configs;
  return parse;
};
/**
 * Combines multiple Parsers into a single Parser.
 *
 * @param parsers Parsers to combine.
 *
 * @returns A Parser with the configs of all provided Parsers.
 */

const composeScales = (...parsers) => {
  const configs = {};

  for (let i = 0; i < parsers.length; i++) Object.assign(configs, parsers[i].configs);

  return scales(configs);
};
/**
 * Intercepts a Component's props and transforms their values using the
 * provided Parser.
 *
 * The functionality of the interceptor is similar to the concept of
 * middleware.
 *
 * @example
 * const scales = composeScales(typographyScales, spaceScales)
 * const interceptor = interceptScales(scales)
 * const Comp = styled('div')(interceptor(compose(typography, space)))
 *
 * @param parser Parser used when intercepting props.
 *
 * @returns A function that accepts a `styled-system` parser.
 */

const interceptScales = parser => styledSystemParser => props => styledSystemParser(parser(props));

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var _marked = /*#__PURE__*/regeneratorRuntime.mark(modularScaleGen),
    _marked2 = /*#__PURE__*/regeneratorRuntime.mark(linearScaleGen);

const cssRegex = /^([+-]?(?:\d+|\d*\.\d+))([a-z]*|%)$/;

const stripUnit = (val, unitReturn = false) => {
  if (typeof val !== 'string') return unitReturn ? [val, undefined] : val;
  const matchedValue = val.match(cssRegex);

  if (unitReturn) {
    if (matchedValue) return [parseFloat(val), matchedValue[2]];
    return [val, undefined];
  }

  if (matchedValue) return parseFloat(val);
  return val;
};
/**
 * Generator function that returns an modular scale values.
 *
 * @param initial Starting/first value of the scale.
 * @param ratio Ratio between values.
 * @param precision Number precision for scale values.
 *
 * @returns Increasing modular scale values on each `next` call.
 */


function modularScaleGen(initial = 0, ratio = 2, precision = 100) {
  var j;
  return regeneratorRuntime.wrap(function modularScaleGen$(_context) {
    while (1) switch (_context.prev = _context.next) {
      case 0:
        j = 0;

      case 1:

        _context.next = 4;
        return Math.round(precision * Math.pow(ratio, j++)) / precision + initial;

      case 4:
        _context.next = 1;
        break;

      case 6:
      case "end":
        return _context.stop();
    }
  }, _marked);
}
/**
 * Generator function that returns linear scale values.
 *
 *
 * @param initial Starting/first value of the scale.
 * @param difference Difference between values.
 * @param precision Number precision for scale values.
 *
 * @returns Increasing linear scale values on each `next` call.
 */

function linearScaleGen(initial = 0, difference = 1, precision = 100) {
  var j;
  return regeneratorRuntime.wrap(function linearScaleGen$(_context2) {
    while (1) switch (_context2.prev = _context2.next) {
      case 0:
        j = 0;

      case 1:

        _context2.next = 4;
        return Math.round(precision * j++ * difference) / precision + initial;

      case 4:
        _context2.next = 1;
        break;

      case 6:
      case "end":
        return _context2.stop();
    }
  }, _marked2);
}
/**
 * Calculates a linear ratio given a min, max, and number of steps to get from min
 * to max.
 *
 * @param min Minimum value in the scale.
 * @param max Maximum value in the scale.
 * @param steps Number of steps to get from `min` to `max` in the scale.
 *
 * @returns Ratio between each step in the linear scale.
 */

const linearRatio = (min, max, steps = 1) => (max - min) / steps;
/**
 * Creates a scale using the provided scaling function.
 *
 * @param count Total number of values in the scale.
 * @param gen Generator function to create values in the scale.
 * @param options Options to configure the scale.
 *
 * @returns An array of values on the scale.
 */

const scale = (count, gen = linearScaleGen(), options = {}) => {
  const {
    unit,
    min = -Infinity,
    max = Infinity,
    transform = val => val
  } = options;
  const arr = [];

  for (let i = 0; i < count; i++) {
    let val = Math.min(max, Math.max(min, transform(gen.next().value, i, count)));
    if (unit === undefined) arr.push(val);else arr.push(val + unit);
  }

  return unit === undefined ? arr : arr;
};
/**
 * Creates a linear scale.
 *
 * @param min The minimum value in the scale.
 * @param max The maximum value in the scale.
 * @param options Options to configure the scale.
 *
 * @returns An array of values on the scale.
 */

const linearScale = (min, max, options = {}) => {
  const {
    precision
  } = options,
        opts = _objectWithoutPropertiesLoose(options, ["precision"]);

  let {
    count,
    difference,
    unit
  } = options;
  if (count && difference) throw new Error('Count and ratio were provided, but only one can be defined.');
  const [minN, minU] = stripUnit(min, true);
  const [maxN, maxU] = stripUnit(max, true);
  if (!unit && minU !== maxU) throw new Error('Units of min and max do not match.');
  unit = unit || minU;

  if (count) {
    difference = linearRatio(minN, maxN, count - 1);
  } else if (difference) {
    count = (maxN - minN) / difference + 1;
  } else {
    count = maxN - minN + 1;
    difference = 1;
  }

  const gen = linearScaleGen(minN, difference, precision);
  return scale(count, gen, _extends({
    unit,
    min: minN,
    max: maxN
  }, opts));
};
/**
 * Negates all values of a scale. Maintains the units of each value.
 *
 * @param scale Scale to negate.
 *
 * @returns The scale with all values negated.
 */

const negateScale = scale => scale.map(x => {
  if (typeof x === undefined) return x;
  const [v, unit] = stripUnit(x, true);
  return unit === undefined ? -v : -v + unit;
});
/**
 * Adds the right scale to the left. Maintains the unit of the left scale. The
 * unit of the right scale is ignored.
 *
 * @param a The left scale. The unit of this scale is used in the new scale.
 * @param b The right scale. The unit of this scale is ignored.
 *
 * @returns A scale with values of pairs from each scale added.
 */

const addScales = (a, b) => {
  const [, unit] = stripUnit(a[0], true);
  const hasUnit = unit !== undefined;
  const result = [];

  for (let i = 0; i < a.length; i++) {
    const ai = stripUnit(a[i] || 0);
    const bi = stripUnit(b[i] || 0);
    result[i] = hasUnit ? ai + bi + unit : ai + bi;
  }

  return result;
};
/**
 * Subtracts the right scale from the left. Maintains the unit of the left
 * scale. The unit of the right scale is ignored.
 *
 * @param a The left scale. The unit of this scale is used in the new scale.
 * @param b The right scale. The unit of this scale is ignored.
 *
 * @returns A scale with values of pairs from each scale subtracted.
 */

const subtractScales = (a, b) => addScales(a, negateScale(b));
/**
 * Merges scale values from the right scale into the left scale. An undefined
 * value in the right scale falls back to the left scale value.
 *
 * @param a The left scale.
 * @param b The right scale.
 *
 * @returns A scale with merged values.
 */

const mergeScalesLeft = (a, b) => {
  const result = [];

  for (let i = 0; i < Math.max(a.length, b.length); i++) result[i] = b[i] === undefined ? a[i] : b[i];

  return result;
};
/**
 * Merges scale values from the left scale into the right scale. An undefined
 * value in the left scale falls back to the right scale value.
 *
 * @param a The left scale.
 * @param b The right scale.
 *
 * @returns A scale with merged values.
 */

const mergeScalesRight = (a, b) => mergeScalesLeft(b, a);

const configs = {
  gridGapScale: {
    systemProp: 'gridGap',
    scale: 'spaceScales'
  },
  gridColumnGapScale: {
    systemProp: 'gridColumnGap',
    scale: 'spaceScales'
  },
  gridRowGapScale: {
    systemProp: 'gridRowGap',
    scale: 'spaceScales'
  }
};
const gridScales = /*#__PURE__*/scales(configs);

const configs$1 = {
  topScale: {
    systemProp: 'top',
    scale: 'spaceScales'
  },
  rightScale: {
    systemProp: 'right',
    scale: 'spaceScales'
  },
  bottomScale: {
    systemProp: 'bottom',
    scale: 'spaceScales'
  },
  leftScale: {
    systemProp: 'left',
    scale: 'spaceScales'
  }
};
const positionScales = /*#__PURE__*/scales(configs$1);

const configs$2 = {};
configs$2.margin = {
  marginScale: {
    systemProp: 'm',
    scale: 'spaceScales'
  },
  marginTopScale: {
    systemProp: 'mt',
    scale: 'spaceScales'
  },
  marginRightScale: {
    systemProp: 'mr',
    scale: 'spaceScales'
  },
  marginBottomScale: {
    systemProp: 'mb',
    scale: 'spaceScales'
  },
  marginLeftScale: {
    systemProp: 'ml',
    scale: 'spaceScales'
  },
  marginXScale: {
    systemProp: 'mx',
    scale: 'spaceScales'
  },
  marginYScale: {
    systemProp: 'my',
    scale: 'spaceScales'
  }
};
configs$2.margin.mScale = configs$2.margin.marginScale;
configs$2.margin.mtScale = configs$2.margin.marginTopScale;
configs$2.margin.mrScale = configs$2.margin.marginRightScale;
configs$2.margin.mbScale = configs$2.margin.marginBottomScale;
configs$2.margin.mlScale = configs$2.margin.marginLeftScale;
configs$2.margin.mxScale = configs$2.margin.marginXScale;
configs$2.margin.myScale = configs$2.margin.marginYScale;
configs$2.padding = {
  paddingScale: {
    systemProp: 'p',
    scale: 'spaceScales'
  },
  paddingTopScale: {
    systemProp: 'pt',
    scale: 'spaceScales'
  },
  paddingRightScale: {
    systemProp: 'pr',
    scale: 'spaceScales'
  },
  paddingBottomScale: {
    systemProp: 'pb',
    scale: 'spaceScales'
  },
  paddingLeftScale: {
    systemProp: 'pl',
    scale: 'spaceScales'
  },
  paddingXScale: {
    systemProp: 'px',
    scale: 'spaceScales'
  },
  paddingYScale: {
    systemProp: 'py',
    scale: 'spaceScales'
  }
};
configs$2.padding.pScale = configs$2.padding.paddingScale;
configs$2.padding.ptScale = configs$2.padding.paddingTopScale;
configs$2.padding.prScale = configs$2.padding.paddingRightScale;
configs$2.padding.pbScale = configs$2.padding.paddingBottomScale;
configs$2.padding.plScale = configs$2.padding.paddingLeftScale;
configs$2.padding.pxScale = configs$2.padding.paddingXScale;
configs$2.padding.pyScale = configs$2.padding.paddingYScale;
const marginScales = /*#__PURE__*/scales(configs$2.margin);
const paddingScales = /*#__PURE__*/scales(configs$2.padding);
const spaceScales = /*#__PURE__*/scales( /*#__PURE__*/_extends({}, configs$2.margin, {}, configs$2.padding));

const configs$3 = {
  fontSizeScale: {
    systemProp: 'fontSize',
    scale: 'fontSizeScales'
  }
};
const typographyScales = /*#__PURE__*/scales(configs$3);

export { addScales, composeScales, gridScales, interceptScales, linearRatio, linearScale, linearScaleGen, marginScales, mergeScalesLeft, mergeScalesRight, modularScaleGen, negateScale, paddingScales, positionScales, scale, scales, spaceScales, subtractScales, typographyScales };
//# sourceMappingURL=styled-system-scale.esm.js.map
